import { FINANCIAL_COMPLIANCE_FIELDS, TURNOVER_ONLY_FIELDS } from "../../config/financialCompliance";

// Section 4 — Administrative Documents: structured Financial Compliance record.
// Ported once from the estimate's Financial Compliance panel at turnover
// creation (see sp_CreateTurnover), then tracked independently by Accounting/
// the turnover team as the actual paperwork (cert numbers, W-9/COI status)
// comes in. Same inline-edit-on-blur pattern as PermitsTermsPanel.

const ALL_FIELDS = [...FINANCIAL_COMPLIANCE_FIELDS, ...TURNOVER_ONLY_FIELDS];

// hiddenKeys: fields another panel answers (layout-2 §4 Contract Requirements).
export default function FinancialCompliancePanel({ compliance, onSave, saving, hiddenKeys }) {
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 4 };
  const ctlStyle = {
    width: "100%", padding: "7px 10px", fontSize: 13,
    border: "1px solid var(--cs-border)", borderRadius: 6,
    background: "var(--surface)", color: "var(--text)", boxSizing: "border-box",
  };

  function field(f) {
    const value = compliance[f.key];
    const onChange = (v) => onSave({ ...compliance, [f.key]: v });

    if (f.type === "boolean") {
      return (
        <div key={f.key} style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={!!value} disabled={saving} onChange={e => onChange(e.target.checked)} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>{f.label}</span>
          </label>
          {f.hint && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{f.hint}</div>}
        </div>
      );
    }

    if (f.type === "select") {
      return (
        <div key={f.key} style={{ marginBottom: 16 }}>
          <div style={labelStyle}>{f.label}</div>
          <select
            value={value || ""}
            disabled={saving}
            onChange={e => onChange(e.target.value || null)}
            style={{ ...ctlStyle, color: value ? "var(--text)" : "var(--text3)" }}
          >
            <option value="">— Select —</option>
            {f.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }

    if (f.type === "textarea") {
      return (
        <div key={f.key} style={{ marginBottom: 16 }}>
          <div style={labelStyle}>{f.label}</div>
          <textarea
            defaultValue={value || ""}
            disabled={saving}
            rows={3}
            onBlur={e => { if ((e.target.value || null) !== (value || null)) onChange(e.target.value || null); }}
            style={{ ...ctlStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
          />
        </div>
      );
    }

    return (
      <div key={f.key} style={{ marginBottom: 16 }}>
        <div style={labelStyle}>{f.label}</div>
        <input
          type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
          defaultValue={f.type === "date" && value ? value.split("T")[0] : (value ?? "")}
          disabled={saving}
          onBlur={e => {
            const v = e.target.value === "" ? null : (f.type === "number" ? +e.target.value : e.target.value);
            if (v !== (value ?? null)) onChange(v);
          }}
          style={ctlStyle}
        />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 24, padding: "16px 18px", background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
        Financial Compliance
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 16 }}>
        Ported from the estimate&rsquo;s Financial Compliance assumptions at turnover creation — update as actual documents come in.
      </div>
      {ALL_FIELDS.filter(f => !(hiddenKeys || []).includes(f.key)).map(field)}
      {saving && <div style={{ fontSize: 11, color: "var(--text3)" }}>Saving…</div>}
    </div>
  );
}
